import React, { useState, useRef, useEffect } from 'react';

/**
 * Custo por nó do pipeline: barras horizontais ranqueadas, uma faceta por provedor.
 *
 * Por que barra e não pizza. Parte-do-todo em pizza só funciona "à primeira vista" e com
 * poucas fatias; o lado da OpenAI tem cinco nós entre US$ 0,009 e US$ 0,020/h e essas
 * fatias ficam indistinguíveis. Pior: duas pizzas separadas esconderiam que um provedor
 * custa o dobro do outro, porque área de pizza não codifica o total. Barras numa ESCALA
 * COMPARTILHADA entregam as duas leituras — o ranking dentro do provedor e a comparação
 * entre provedores — e ainda acomodam nome comprido de slug como rótulo de linha.
 *
 * Uma cor só, não uma por nó: a comparação aqui é de magnitude, não de identidade. E é a
 * mesma azul que significa "LLM" nos outros gráficos da página — todas estas barras SÃO
 * custo de LLM, então a cor continua querendo dizer a mesma coisa.
 */

const COR = '#2a78d6';        // slot categórico 1 — o mesmo "LLM" dos outros gráficos
const SUPERFICIE = '#ffffff';
const GRADE = '#e9ecef';
const EIXO = '#adb5bd';
const TEXTO_1 = '#212529';
const TEXTO_2 = '#6c757d';
const ALERTA = '#7a5b00';

const M = { top: 8, right: 172, bottom: 34, left: 196 };
const LINHA = 25;             // altura da faixa de cada nó
const BARRA = 14;
const TITULO_FACETA = 26;
const PASSOS = [0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10];

const escalaBoa = (max) => {
    for (const p of PASSOS) {
        if (max / p <= 5) return { passo: p, topo: Math.ceil(max / p) * p || p };
    }
    const p = PASSOS[PASSOS.length - 1];
    return { passo: p, topo: Math.ceil(max / p) * p };
};

const fmtBRL = (v, dec = 3) =>
    `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

const num = (v, dec = 0) =>
    v == null ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });

// SVG não trunca texto: encurta no meio, que preserva o começo e o fim do slug.
const encurta = (s, max = 26) =>
    s.length <= max ? s : `${s.slice(0, max - 10)}…${s.slice(-8)}`;

const provedorDe = (model) => {
    if (!model) return 'Outro';
    if (model.startsWith('claude')) return 'Anthropic';
    if (model.startsWith('gpt') || model.startsWith('o1') || model.startsWith('o3')) return 'OpenAI';
    return 'Outro';
};

// Cada métrica responde uma pergunta diferente, e uma delas não admite porcentagem: somar
// o custo médio POR ATENDIMENTO entre nós de coberturas diferentes não dá o atendimento
// médio, dá o custo de um atendimento hipotético que rodou tudo.
const METRICAS = {
    hora: { campo: 'usd_per_hour', dec: 3, pct: true,
            eixo: 'custo por hora de atendimento (contribuição do nó)' },
    atendimento: { campo: 'avg_cost_usd', dec: 3, pct: false,
                   eixo: 'custo médio por atendimento em que o nó rodou' },
    total: { campo: 'total_cost_usd', dec: 2, pct: true,
             eixo: 'gasto absoluto no período' },
};

function GraficoNos({ stages, taxa, metrica = 'hora' }) {
    const box = useRef(null);
    const [largura, setLargura] = useState(900);
    const [hover, setHover] = useState(null);

    // O observer precisa ser instalado mesmo quando ainda nao ha dado para desenhar. Antes
    // havia `return` antecipado ANTES do ref ser anexado, e como `taxa` comeca nula (a cotacao
    // chega depois, da API), no primeiro render o ref era null, o efeito saia vazio e nunca
    // mais rodava — a largura ficava travada no valor inicial do useState, metade da tela numa
    // tela larga. Por isso o wrapper com o ref e SEMPRE renderizado, e o estado vazio vai
    // dentro dele.
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([e]) => setLargura(Math.max(560, e.contentRect.width)));
        const el = box.current;
        if (el) ro.observe(el);
        return () => ro.disconnect();
    }, [stages, taxa]);

    const vazio = (msg) => (
        <div className="gc-wrap" ref={box}><div className="gc-vazio">{msg}</div></div>
    );
    if (!stages || stages.length === 0) return vazio('Sem análises no período.');
    if (!taxa) return vazio('Informe a cotação do dólar para ver os valores.');

    const m = METRICAS[metrica] || METRICAS.hora;
    const linhas = stages
        .map((st) => ({ ...st, provedor: provedorDe(st.model), valor: (st[m.campo] || 0) * taxa }))
        .filter((st) => st.valor > 0);

    if (linhas.length === 0) return <div className="gc-vazio">Sem custo apurado no período.</div>;

    const totalGeral = linhas.reduce((a, l) => a + l.valor, 0);
    const ordemProv = ['Anthropic', 'OpenAI', 'Outro'];
    const facetas = ordemProv
        .map((nome) => ({
            nome,
            itens: linhas.filter((l) => l.provedor === nome).sort((a, b) => b.valor - a.valor),
        }))
        .filter((f) => f.itens.length > 0)
        .map((f) => ({ ...f, total: f.itens.reduce((a, i) => a + i.valor, 0) }));

    const { passo, topo } = escalaBoa(Math.max(...linhas.map((l) => l.valor)));
    const larguraPlot = largura - M.left - M.right;
    const x = (v) => M.left + (v / topo) * larguraPlot;

    const alturaFacetas = facetas.reduce((a, f) => a + TITULO_FACETA + f.itens.length * LINHA, 0);
    const altura = M.top + alturaFacetas + M.bottom;

    const ticks = [];
    for (let v = 0; v <= topo + 1e-9; v += passo) ticks.push(v);

    // Posição vertical de cada linha, percorrendo as facetas em ordem.
    let cursor = M.top;
    const postas = [];
    facetas.forEach((f) => {
        const yTitulo = cursor + TITULO_FACETA - 9;
        cursor += TITULO_FACETA;
        const daFaceta = f.itens.map((it) => {
            const y = cursor;
            cursor += LINHA;
            return { ...it, y };
        });
        postas.push({ ...f, yTitulo, itens: daFaceta });
    });

    return (
        <div className="gc-wrap" ref={box}>
            <svg width={largura} height={altura} role="img"
                 aria-label={`Por nó de LLM do pipeline, agrupado por provedor: ${m.eixo}`}>
                <rect x={M.left} y={M.top} width={larguraPlot} height={alturaFacetas} fill={SUPERFICIE} />

                {/* grade + eixo, escala COMPARTILHADA entre as facetas */}
                {ticks.map((v) => (
                    <g key={v}>
                        <line x1={x(v)} x2={x(v)} y1={M.top} y2={M.top + alturaFacetas}
                              stroke={v === 0 ? EIXO : GRADE} strokeWidth={1} />
                        <text x={x(v)} y={altura - M.bottom + 18} textAnchor="middle"
                              fontSize={11} fill={TEXTO_2}>
                            {fmtBRL(v, v >= 10 ? 0 : (v >= 1 ? 2 : 3))}
                        </text>
                    </g>
                ))}
                <text x={M.left + larguraPlot / 2} y={altura - 4} textAnchor="middle"
                      fontSize={10} fill={TEXTO_2}>
                    {m.eixo}
                </text>

                {postas.map((f) => (
                    <g key={f.nome}>
                        <text x={8} y={f.yTitulo} fontSize={12} fontWeight={700} fill={TEXTO_1}>
                            {f.nome}
                        </text>
                        <text x={M.left} y={f.yTitulo} fontSize={11} fill={TEXTO_2}>
                            {fmtBRL(f.total, m.dec)}{metrica === 'hora' ? '/h' : ''}
                            {m.pct ? ` · ${Math.round((f.total / totalGeral) * 100)}% do LLM` : ' (soma)'}
                        </text>

                        {f.itens.map((it) => {
                            const chave = `${it.slug}-${it.model}`;
                            const apagado = hover != null && hover !== chave;
                            const largBarra = Math.max(1, x(it.valor) - M.left);
                            return (
                                <g key={chave} opacity={apagado ? 0.45 : 1}>
                                    <title>{`${it.slug} · ${it.model}`}</title>
                                    <text x={M.left - 10} y={it.y + BARRA - 2} textAnchor="end"
                                          fontSize={11} fill={TEXTO_1}>
                                        {encurta(it.slug)}
                                    </text>
                                    <rect x={M.left} y={it.y} width={largBarra} height={BARRA}
                                          fill={COR} rx={4} />
                                    <text x={M.left + largBarra + 8} y={it.y + BARRA - 2}
                                          fontSize={11} fill={TEXTO_1}>
                                        {fmtBRL(it.valor, m.dec)}
                                        {m.pct && (
                                            <tspan fill={TEXTO_2}>
                                                {'  '}{Math.round((it.valor / totalGeral) * 100)}%
                                            </tspan>
                                        )}
                                    </text>
                                    {/* Cache zero é o achado acionável: ícone E texto, nunca só cor. */}
                                    {!it.cached_pct && (
                                        <text x={M.left + largBarra + 8} y={it.y + BARRA + 10}
                                              fontSize={9.5} fill={ALERTA}>
                                            ⚠ sem cache
                                        </text>
                                    )}
                                    {/* alvo do hover maior que a barra: a faixa inteira */}
                                    <rect x={0} y={it.y - 5} width={largura} height={LINHA}
                                          fill="transparent" tabIndex={0}
                                          onMouseEnter={() => setHover(chave)} onFocus={() => setHover(chave)}
                                          onMouseLeave={() => setHover(null)} onBlur={() => setHover(null)} />
                                </g>
                            );
                        })}
                    </g>
                ))}
            </svg>

            {hover != null && (() => {
                const it = linhas.find((l) => `${l.slug}-${l.model}` === hover);
                if (!it) return null;
                return (
                    <div className="gc-tooltip gc-tooltip-nos">
                        <div className="gc-tt-titulo">{it.slug} · {it.model}</div>
                        {[['hora', 'usd_per_hour', '/ hora', 3],
                          ['atendimento', 'avg_cost_usd', '/ atendimento', 3],
                          ['total', 'total_cost_usd', 'total no período', 2]]
                            // a métrica escolhida vem primeiro, para o olho achá-la no mesmo lugar
                            .sort((a, b) => (a[0] === metrica ? -1 : b[0] === metrica ? 1 : 0))
                            .map(([k, campo, rot, dec]) => (
                                <div className="gc-tt-linha" key={k}>
                                    <span className="gc-tt-valor">
                                        {fmtBRL((it[campo] || 0) * taxa, dec)}
                                    </span>
                                    <span className="gc-tt-nome">{rot}</span>
                                </div>
                            ))}
                        <div className="gc-tt-pe">
                            {it.cached_pct
                                ? `cache ${it.cached_pct}% do input · ${num(it.avg_cache_read_tokens)} lidos/atend`
                                : 'sem uso de cache — input pago a preço cheio'}
                            <br />
                            {num(it.avg_uncached_tokens)} input s/ cache · {num(it.avg_output_tokens)} output
                            {' · '}{num(it.avg_calls, 1)} chamada(s)
                            <br />
                            {num(it.transcriptions)} atendimentos
                            {it.coverage_pct != null ? ` (${it.coverage_pct}% do período)` : ''}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default GraficoNos;
